/**
 * Runtime wiring / application bootstrap.
 *
 * Responsibilities of this module (high level):
 *  - Construct (or hydrate) a {@link SimulationEngine} instance from persisted snapshots & current UI settings.
 *  - Initialize Three.js scene, camera, controls, HUD and visual helpers (instanced spheres, arrows, trajectories).
 *  - Bridge engine events (frame, diagnostics, wrap) to visual update routines and lightweight persistence.
 *  - Maintain reference-frame handling (sun / center–of–mass) via a per-frame translation vector so the *authoritative*
 *    simulation state remains in an inertial coordinate system while the user can view a centered frame.
 *  - Expose a small debug / test surface on `window.__mdjs` (intentionally unstable – for integration tests only).
 *  - Handle localStorage save / load of engine core state, UI settings, and visual metadata (colors, trajectories).
 *
 * Architectural notes:
 *  - The physics layer owns all particle state in SoA buffers; this file never mutates forces directly – it seeds
 *    initial buffers then treats them as read‑only (except for visual centering math).
 *  - Display-space (reference-frame shifted) positions are maintained in a separate Float32Array so automated tests
 *    can assert centered coordinates without altering the engine's canonical state.
 *  - Most per-particle visual work (mesh transforms, arrow updates, trajectory ring buffer management) is delegated
 *    to specialized modules under `src/visual/` to keep this driver focused on orchestration.
 */
import { settings } from './control/settings.js'
import { init, ifMobileDevice } from './init.js'
import { toggle } from './control/panel.js'
import { ConfigModal } from './control/modal.js'
import { saveToLocal, loadEngineFromLocal } from './engine/persist.js'
import { loadSettingsFromLocal, saveSettingsToLocal } from './control/persist.js'
import { saveVisualDataToLocal, loadVisualDataFromLocal } from './visual/persist.js'
import { showToast, clearToastFlags } from './ui/toast.js'
import { Scene, Camera, PerspectiveCamera, WebGLRenderer, Color, Vector3, BufferAttribute } from 'three'
// New SoA simulation core imports
import { createState, seedInitialState, type SimulationState } from './core/simulation/state.js'
import { generateMassesCharges, generatePositions } from './core/simulation/seeding.js'
import type { Diagnostics } from './core/simulation/diagnostics.js'
// Experimental engine
import { SimulationEngine } from './engine/SimulationEngine.js'
import { buildEngineConfig } from './engine/config.js'
import { initSettingsSync, pushSettingsToEngine, registerAutoPush, AUTO_PUSH_KEYS } from './engine/settingsSync.js'
import { InstancedSpheres } from './visual/InstancedSpheres.js'
import { trajectories, ensureTrajectories, shouldShiftTrajectory, markTrajectorySnapshot, updateTrajectoryBuffer } from './visual/trajectory.js'
import { createArrows, updateScaleBars, finalizeArrows, type ArrowSet } from './visual/arrows.js'
import { getHud } from './visual/hud.js'
import { computeCircularOrbitVelocity } from './core/simulation/orbitInit.js'
import { makeClonePositionsList, minimumImagePoint } from './core/pbc.js'
import { computeFrameOffset, updateDisplayPositions } from './core/frameOfReference.js'
import { createWrapMarker, updateWrapMarkers, type WrapEventRecord } from './visual/wrapMarkers.js'
import { radiusFromMass } from './util/vectorMath.js'
import { SettingsObject } from './control/settingsSchema.js'

// global variables
interface StereoEffectLike { render(scene: Scene, camera: Camera): void; setSize?(w: number, h: number): void }
interface ControlsLike { update(): void }
interface StatsLike { update(): void }
interface TemperaturePanelLike { update(t: number, max: number): void }

let camera: PerspectiveCamera
let scene: Scene
let renderer: WebGLRenderer
let effect: StereoEffectLike | undefined
let controls: ControlsLike | undefined
let temperaturePanel: TemperaturePanelLike
let stats: StatsLike
let maxTemperature = 0
// New SoA simulation objects
let engine: SimulationEngine | undefined
let simState: SimulationState | undefined
let lastDiagnostics: Diagnostics | undefined
// Batched per-particle arrows managed via arrows module
let arrows: ArrowSet | undefined
let sphereMesh: InstancedSpheres | undefined
let sphereCloneMesh: InstancedSpheres | undefined
// Portal wrap markers moved to visual/wrapMarkers.ts

// Display-space (frame-offset adjusted) positions exposed via window.__mdjs.simState.positions
// so integration tests can reason about reference frame behaviors without mutating engine state.
let displayPositions: Float32Array | undefined

// Array of per-particle colors.
const colors: Color[] = []
// Trajectories managed in visual/trajectory.ts
let time = 0
// Track previous frame offset to adjust trajectory history on wrap events.
const lastFrameOffset = new Vector3(0, 0, 0)

// Expose minimal state for headless smoke tests (non-production usage). We install a stub immediately
// (before DOM ready) so test harness code that runs right after the load event always sees an object.
declare global { interface Window { __mdjs?: { colors: Color[]; settings: SettingsObject; simState?: SimulationState; diagnostics?: Diagnostics; ready?: boolean }; __pauseEngine?: () => void } }
if (typeof window !== 'undefined') {
  // Preserve any existing reference (in case of hot reload) and just update fields.
  const api = window.__mdjs || (window.__mdjs = { colors, settings })
  api.colors = colors
  api.settings = settings
  api.ready = false
}

/**
 * el: the DOM element you'd like to test for visibility.
 */
function isVisible(el: HTMLElement | null): boolean {
  return !!el && window.getComputedStyle(el).display !== 'none'
}


const _tmpFrom = new Vector3()

// Use shared minimumImagePoint (core/pbc) instead of local reimplementation
function minimumImageVec(out: Vector3, x: number, y: number, z: number): Vector3 {
  out.set(x, y, z)
  return settings.if_use_periodic_boundary_condition
    ? minimumImagePoint(out, { x: settings.spaceBoundaryX, y: settings.spaceBoundaryY, z: settings.spaceBoundaryZ })
    : out
}


/**
 * Per-frame update of per-particle HUD rows, trajectories and instanced sphere transforms.
 * Delegates to more granular helpers but centralizes the iteration so we only loop N once here.
 */
function updateFromSimulation(frameOffset: Vector3): void {
  if (!engine || !simState) return
  const hudVisible = isVisible(document.querySelector('#hud'))
  const needsTrajectoryShift = shouldShiftTrajectory(time)
  const perForce = engine.getPerForceContributions()
  for (let i = 0; i < colors.length; i++) updateOneParticle(i, hudVisible, needsTrajectoryShift, frameOffset, perForce)
  if (sphereMesh && simState) updateSpheres(frameOffset)
}

/**
 * Update HUD + (optionally) trajectory state for a single particle index.
 *
 * Side effects:
 *  - Mutates trajectory ring buffer attribute when a shift is due.
 *  - Writes per-particle diagnostics into HUD table (if visible).
 */
function updateOneParticle(i: number, hudVisible: boolean, needsTrajectoryShift: boolean, frameOffset: Vector3, perForce: Record<string, Float32Array>): void {
  if (!simState) return
  const { positions, velocities, forces, masses, charges } = simState
  // Prefer authoritative escaped flag from core state over legacy per-Particle flag.
  if (simState.escaped && simState.escaped[i] === 1) return
  const i3 = 3 * i
  // Raw position in frame coordinates (pre minimum-image)
  const rawRelX = positions[i3] - frameOffset.x
  const rawRelY = positions[i3 + 1] - frameOffset.y
  const rawRelZ = positions[i3 + 2] - frameOffset.z
  const traj = settings.if_showTrajectory ? trajectories[i] : null
  const trajectoryAttr = (settings.if_showTrajectory && traj)
    ? traj.geometry.getAttribute('position') as BufferAttribute
    : null
  // For trajectory continuity store the pre-minimum-image relative position
  if (trajectoryAttr && needsTrajectoryShift) updateTrajectoryBuffer(_tmpFrom.set(rawRelX, rawRelY, rawRelZ), trajectoryAttr, settings.maxTrajectoryLength)
  const vx = velocities[i3], vy = velocities[i3 + 1], vz = velocities[i3 + 2]
  const fx = forces[i3], fy = forces[i3 + 1], fz = forces[i3 + 2]
  // Store final (display) position for tests & capture
  // (no longer caching display-space positions)
  if (hudVisible) getHud()?.update(i, { mass: masses[i] || 1, charge: charges[i] || 0, vx, vy, vz, fx, fy, fz, perForce })
}

/**
 * Update both primary and (if PBC enabled) clone instanced sphere batches.
 * Separated from `updateFromSimulation` to make early return conditions explicit.
 */
function updateSpheres(frameOffset: Vector3): void {
  if (!simState || !sphereMesh) return
  renderPrimarySpheres(frameOffset)
  renderCloneSpheres(frameOffset)
}

/**
 * Write per-instance transform + color for main simulation particles.
 * Applies current reference-frame offset and (optionally) minimum‑image wrapping before committing.
 */
function renderPrimarySpheres(frameOffset: Vector3): void {
  if (!simState || !sphereMesh) return
  const { positions, masses, N, escaped } = simState
  const tmpPos = new Vector3()
  for (let i = 0; i < N; i++) {
    if (escaped && escaped[i] === 1) { sphereMesh.update(i, tmpPos.set(0, -9999, 0), 0.0001, new Color(0x333333)); continue }
    const i3 = 3 * i
    tmpPos.set(positions[i3] - frameOffset.x, positions[i3 + 1] - frameOffset.y, positions[i3 + 2] - frameOffset.z)
    if (settings.if_use_periodic_boundary_condition) {
      minimumImageVec(tmpPos, tmpPos.x, tmpPos.y, tmpPos.z)
    }
    const m = masses[i] || 1
    const radius = radiusFromMass(m)
    sphereMesh.update(i, tmpPos, radius, colors[i] || new Color(0xffffff))
  }
  sphereMesh.commit()
}

/**
 * Render semi‑transparent periodic image copies (26 neighbor cells) when PBC is active.
 * Each original particle spawns one instance per neighbor cell offset.
 */
function renderCloneSpheres(frameOffset: Vector3): void {
  if (!simState || !sphereCloneMesh) return
  const visible = settings.if_use_periodic_boundary_condition
  sphereCloneMesh.setVisible(visible)
  if (!visible) return
  const { positions, masses, N, escaped } = simState
  const cloneOffsets = makeClonePositionsList(settings.spaceBoundaryX, settings.spaceBoundaryY, settings.spaceBoundaryZ)
  const clonePos = new Vector3()
  const basePos = new Vector3()
  let baseIndex = 0
  for (const offset of cloneOffsets) {
    for (let i = 0; i < N; i++) {
      if (escaped && escaped[i] === 1) { sphereCloneMesh.update(baseIndex + i, clonePos.set(0, -9999, 0), 0.0001, new Color(0x333333)); continue }
      const i3 = 3 * i
      basePos.set(positions[i3] - frameOffset.x, positions[i3 + 1] - frameOffset.y, positions[i3 + 2] - frameOffset.z)
      if (settings.if_use_periodic_boundary_condition) minimumImageVec(basePos, basePos.x, basePos.y, basePos.z)
      clonePos.set(basePos.x + offset.x, basePos.y + offset.y, basePos.z + offset.z)
      const m = masses[i] || 1
      const radius = radiusFromMass(m)
      sphereCloneMesh.update(baseIndex + i, clonePos, radius, colors[i] || new Color(0xffffff))
    }
    baseIndex += N
  }
  sphereCloneMesh.commit()
}

/**
 * Advance simulation one timestep and update visual + diagnostic layers.
 * Split from animate() to keep the frame logic testable and reduce complexity warnings.
 */
function applyVisualUpdates(): void {
  updateScaleBars(lastDiagnostics)
  const frameOffset = computeFrameOffset(simState, settings)
  // Update display-space positions first so per-particle logic & HUD see centered coordinates if needed.
  displayPositions = updateDisplayPositions(simState, frameOffset, displayPositions)
  updateFromSimulation(frameOffset)
  updateWrapMarkers(scene)
  // Update / toggle arrows after particle positions refreshed
  if (arrows) finalizeArrows(arrows, simState, lastDiagnostics, frameOffset)
  if (shouldShiftTrajectory(time)) markTrajectorySnapshot(time)
  // Temperature from diagnostics (fallback to 0 if undefined) & track peak
  if (lastDiagnostics) {
    const T = lastDiagnostics.temperature
    if (T > maxTemperature) maxTemperature = T
    temperaturePanel.update(T, maxTemperature)
  }
  update(); render(renderer, effect); stats.update()
  lastFrameOffset.copy(frameOffset)
}

/**
 * Per-frame camera / controls maintenance (projection matrix updates & orbit control damping).
 */
function update(): void {
  // (removed stale resize comment)
  camera.updateProjectionMatrix()
  if (controls) controls.update()
}

/**
 * Render the Three.js scene graph either via stereo (mobile VR) effect or standard renderer.
 */
function render(renderer: WebGLRenderer, effect: StereoEffectLike | undefined): void {
  if (ifMobileDevice && effect) {
    effect.render(scene, camera)
  } else {
    renderer.render(scene, camera)
  }
}

// when document is ready:
// Source: https://stackoverflow.com/a/9899701/1147061
function docReady(fn: () => void): void {
  // If DOM is already parsed, execute immediately (avoid next‑tick race that caused tests to read
  // an uninitialized __mdjs stub). Otherwise defer until DOMContentLoaded.
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    fn()
  } else {
    document.addEventListener('DOMContentLoaded', fn)
  }
}
docReady(() => {
  console.log('Ready.')
  // Attempt to hydrate engine first (if snapshot present) BEFORE building visual particle system.
  // Load persisted user settings first so fresh world uses them if no snapshot.
  loadSettingsFromLocal()
  const loaded = loadEngineFromLocal()
  if (loaded) {
    console.log('Engine loaded from localStorage.')
    engine = loaded.engine
    // Mirror loaded config into settings (best-effort) – future: migrate settings <-> engine properly.
    settings.particleCount = loaded.snapshot.config.world.particleCount
    settings.spaceBoundaryX = loaded.snapshot.config.world.box.x
    settings.spaceBoundaryY = loaded.snapshot.config.world.box.y
    settings.spaceBoundaryZ = loaded.snapshot.config.world.box.z
    settings.dt = loaded.snapshot.config.runtime.dt
    settings.cutoffDistance = loaded.snapshot.config.runtime.cutoff
    time = loaded.snapshot.time
    // Trajectories persisted separately (visual aid) – defer restore after visuals ready.
  }

  const initResult = init(settings, colors)
  scene = initResult.scene
  camera = initResult.camera
  renderer = initResult.renderer
  controls = initResult.controls
  stats = initResult.stats
  temperaturePanel = initResult.temperaturePanel
  effect = initResult.effect

  if (!engine) {
    console.log('No engine found, creating a new one.')
    // Fresh run: construct engine from particles
    simState = createState({
      particleCount: settings.particleCount,
      box: { x: settings.spaceBoundaryX, y: settings.spaceBoundaryY, z: settings.spaceBoundaryZ },
      dt: settings.dt,
      cutoff: settings.cutoffDistance
    })
    // Reintroduce randomized mass / charge seeding (core SoA ownership)
    const { masses: seedMasses, charges: seedCharges } = generateMassesCharges({
      N: simState.N,
      massLower: settings.massLowerBound,
      massUpper: settings.massUpperBound,
      chargeOptions: [...settings.availableCharges],
      sunMass: settings.sunMass,
      makeSun: settings.if_makeSun
    })
    const seedPositions = generatePositions({
      N: simState.N,
      bounds: { x: settings.spaceBoundaryX, y: settings.spaceBoundaryY, z: settings.spaceBoundaryZ },
      makeSun: settings.if_makeSun
    })
    seedInitialState(simState, { positions: seedPositions, masses: seedMasses, charges: seedCharges })
    // If a central "sun" is requested, assign approximate circular orbit velocities
    // for all other particles instead of leaving them at rest. This reduces the
    // extreme radial infall + sling ejection that occurs when starting from zero
    // velocity near a 1/r^2 singular attraction.
    if (settings.if_makeSun && simState.N > 1) {
      console.log('Seeding circular orbit velocities for particles.')
      const Msun = simState.masses[0] || settings.sunMass || 1
      const G = settings.G
      const pos = simState.positions
      const vel = simState.velocities
      for (let i = 1; i < simState.N; i++) {
        const i3 = 3 * i
        const { vx, vy, vz } = computeCircularOrbitVelocity(pos[i3], pos[i3 + 1], pos[i3 + 2], Msun, G)
        vel[i3] = vx; vel[i3 + 1] = vy; vel[i3 + 2] = vz
      }
    }
    // Build engine config and manually assign particleCount (not auto-bound)
    const config = buildEngineConfig(settings)
    config.world.particleCount = settings.particleCount
    engine = new SimulationEngine(config)
    engine.seed({ positions: simState.positions, velocities: simState.velocities, masses: simState.masses, charges: simState.charges })
  }
  simState = engine.getState()
  // Clear toast flags on fresh start/reload so warnings can appear again
  clearToastFlags()
  // Allocate initial display-space positions & populate once before exposing API (ensures tests see centered values).
  displayPositions = new Float32Array(simState.positions.length)
  displayPositions = updateDisplayPositions(simState, computeFrameOffset(simState, settings), displayPositions)
  // Expose early so even if later visual initialization fails tests can still access simulation state.
  const earlyApi = window.__mdjs || (window.__mdjs = { colors, settings })
  earlyApi.simState = simState
  if (displayPositions) (earlyApi.simState as SimulationState & { positions: Float32Array }).positions = displayPositions
  initSettingsSync(engine)
  // Auto-push: wrap selected mutable settings with setters triggering engine.updateConfig
  registerAutoPush(engine, AUTO_PUSH_KEYS)
  engine.on('frame', ({ time: t }) => { time = t; applyVisualUpdates() })
  // When engine performs periodic wrapping, translate corresponding trajectory history
  // so rendered path remains continuous (avoid long teleport segments across box).
  const handleWrapEvent = ({ wraps }: { wraps: WrapEventRecord[] }) => {
    if (!settings.if_showTrajectory || wraps.length === 0) return
    const newFrameOffset = computeFrameOffset(simState, settings)
    const dFx = newFrameOffset.x - lastFrameOffset.x
    const dFy = newFrameOffset.y - lastFrameOffset.y
    const dFz = newFrameOffset.z - lastFrameOffset.z
    for (const w of wraps) applyWrapShift(w, dFx, dFy, dFz, newFrameOffset)
    lastFrameOffset.copy(newFrameOffset)
  }
  const applyWrapShift = (w: WrapEventRecord, dFx: number, dFy: number, dFz: number, newFrameOffset: Vector3) => {
    const line = trajectories[w.i]
    if (!line) return
    const attr = line.geometry.getAttribute('position') as BufferAttribute
    const shiftX = w.dx - dFx
    const shiftY = w.dy - dFy
    const shiftZ = w.dz - dFz
    if (shiftX === 0 && shiftY === 0 && shiftZ === 0) return
    const len = settings.maxTrajectoryLength
    for (let j = 0; j < len; j++) attr.setXYZ(j, attr.getX(j) + shiftX, attr.getY(j) + shiftY, attr.getZ(j) + shiftZ)
    attr.needsUpdate = true
    if (!scene || !settings.if_use_periodic_boundary_condition) return
    // Use precise crossing exit + entry points from engine payload.
    for (const c of w.crossings) {
      // Get the velocity of the particle at the time of wrap
      // Use w.i as the particle index
      const i3 = 3 * w.i
      const velocities = simState?.velocities
      const v = velocities ? { x: velocities[i3], y: velocities[i3 + 1], z: velocities[i3 + 2] } : { x: 0, y: 0, z: 1 }
      createWrapMarker(scene, { axis: c.axis, sign: c.sign }, c.exit, newFrameOffset, 0xffaa00, v)
      createWrapMarker(scene, { axis: c.axis, sign: (c.sign * -1) as 1 | -1 }, c.entry, newFrameOffset, 0x007bff, v)
    }
  }
  engine.on('wrap', handleWrapEvent)
  engine.on('diagnostics', (d) => { lastDiagnostics = d; if (window.__mdjs) window.__mdjs.diagnostics = d })

  // Listen for numerical instability events and display toast notifications
  engine.on('instability', (result) => {
    const { level, message, diagnostics, suggestions } = result

    // Format diagnostic details for display
    const details = [
      `Speed: ${diagnostics.maxSpeed.toFixed(2)}`,
      `Force: ${diagnostics.maxForceMag.toFixed(2)}`,
      `Energy: ${diagnostics.total.toFixed(2)}`,
      `Temp: ${diagnostics.temperature.toFixed(1)}`
    ].join(' | ')

    // Map level to title and toast type
    let title = ''
    let toastType: 'critical' | 'severe' | 'warning' = 'warning'

    if (level === 'critical') {
      title = '🚨 Simulation Crashed'
      toastType = 'critical'
    } else if (level === 'severe') {
      title = '⚠️ Severe Instability'
      toastType = 'severe'
    } else if (level === 'warning') {
      title = '⚠️ Possible Instability'
      toastType = 'warning'
    }

    showToast({
      type: toastType,
      title,
      message,
      details,
      suggestions,
      persistent: true
    })
  })

  // In headless test (puppeteer) environment requestAnimationFrame may be stubbed / throttled; fall back to interval.
  const useRaf = typeof requestAnimationFrame === 'function'

  engine.run({ useRaf })
  // Create arrow visualizers once state & scene are ready
  if (simState) {
    // Ensure trajectories created/hidden per settings
    ensureTrajectories(simState, colors, scene)
    arrows = createArrows(simState.N, scene)
    sphereMesh = new InstancedSpheres(simState.N, { baseRadius: 0.1 })
    sphereMesh.addTo(scene)
    // Clone spheres (26 neighbor cells)
    const cloneOffsets = makeClonePositionsList(settings.spaceBoundaryX, settings.spaceBoundaryY, settings.spaceBoundaryZ)
    // Make out-of-boundary (periodic clone) particles semi-transparent for visual distinction.
    sphereCloneMesh = new InstancedSpheres(simState.N * cloneOffsets.length, { baseRadius: 0.1, opacity: 0.5 })
    sphereCloneMesh.addTo(scene)
    // Hide any legacy point-sprite based objects (main + periodic clones) to avoid lingering dots.
    scene.traverse(obj => { if ((obj as unknown as { isPoints?: boolean }).isPoints) obj.visible = false })
    // Initialize sphere transforms/colors immediately
    updateSpheres(new Vector3(0, 0, 0))
    // Attempt to restore persisted visual data (colors always; trajectories only if enabled)
    loadVisualDataFromLocal(settings.if_showTrajectory ? trajectories : [], colors)
  }
  const api = window.__mdjs || (window.__mdjs = { colors, settings })
  api.simState = simState
  if (displayPositions) (api.simState as SimulationState & { positions: Float32Array }).positions = displayPositions
  api.diagnostics = lastDiagnostics
  api.ready = true
  window.__pauseEngine = () => { engine?.pause() }

  // Setup HUD interaction handlers
  const hud = getHud()
  if (hud && simState && camera && controls) {
    const hudSimState = simState // Capture for closure
    const hudControls = controls // Capture for closure

    // Handle particle click for camera focus
    hud.setParticleClickHandler((particleIndex: number) => {
      const i3 = particleIndex * 3
      const pos = displayPositions || hudSimState.positions
      const targetX = pos[i3]
      const targetY = pos[i3 + 1]
      const targetZ = pos[i3 + 2]

      // Smoothly move camera to focus on particle
      const targetPos = new Vector3(targetX, targetY, targetZ)
      const distance = 5 // Distance from particle
      const direction = camera.position.clone().sub(targetPos).normalize()
      const newCameraPos = targetPos.clone().add(direction.multiplyScalar(distance))

      // Animate camera movement (simple linear interpolation)
      const startPos = camera.position.clone()
      const steps = 30
      let step = 0

      const animate = () => {
        step++
        const t = step / steps
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2 // easeInOutQuad
        camera.position.lerpVectors(startPos, newCameraPos, eased)

        // Update orbit controls target if available
        if ('target' in hudControls && hudControls.target instanceof Vector3) {
          hudControls.target.copy(targetPos)
        }

        if (step < steps) {
          requestAnimationFrame(animate)
        }
      }
      animate()
    })

    // Handle row selection for highlighting (optional - can be extended later)
    hud.setParticleSelectHandler((particleIndex: number | null) => {
      // Could be used to highlight the selected particle in 3D view
      // For now, just log it for debugging
      if (particleIndex !== null) {
        console.log(`Selected particle ${particleIndex}`)
      }
    })
  }

  // Initialize configuration modal
  let configModal: ConfigModal | null = null
  try {
    configModal = new ConfigModal(settings)
    configModal.initialize()
  } catch (e) {
    console.error('[script.ts] Failed to initialize config modal:', e)
  }

  // Install full-state persistence handler (overrides placeholder in init.js)
  window.onbeforeunload = () => {
    try { saveSettingsToLocal() } catch { /* ignore */ }
    if (engine) {
      // Collect trajectory buffers (if any) for persistence
      // Always persist colors (and trajectories if present) regardless of toggle state
      saveVisualDataToLocal(trajectories, colors)
      saveToLocal(engine)
    }
  }
  // bind keyboard event:
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      toggle('#hud')
    }
    if (e.key === 'c' || e.key === 'C') {
      e.preventDefault()
      if (configModal) configModal.show()
    }
    if (e.key === 'p') { // quick dev key: push current settings to engine
      if (engine) pushSettingsToEngine(engine)
    }
  })
})

// Clone position list now supplied by shared pbc module (returns plain objects); adapt to Vector3 instances on demand.
// For existing usage we map to Vector3 once per invocation.
